import type { ReactNode } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import FdeSelect from '@/frontend/features/Fde/FdeSelect'
import type { Option } from '@/frontend/features/Fde/types'

const controlClass = 'mt-1.5 w-full'

export function FormField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-sm font-medium text-foreground">
      <span>{label}</span>
      {children}
    </label>
  )
}

export function TextControl(props: React.ComponentProps<typeof Input> & { label: string }) {
  const { label, ...inputProps } = props
  return <FormField label={label}><Input {...inputProps} className={`${controlClass} ${inputProps.className ?? ''}`} /></FormField>
}

export function TextareaControl(props: React.ComponentProps<typeof Textarea> & { label: string }) {
  const { label, ...textareaProps } = props
  return <FormField label={label}><Textarea {...textareaProps} className={`${controlClass} ${textareaProps.className ?? ''}`} /></FormField>
}

export function SelectControl({ label, value, onChange, options, placeholder, disabled }: {
  label: string
  value: string
  onChange: (value: string) => void
  options: Option[]
  placeholder?: string
  disabled?: boolean
}) {
  return (
    <div className="block text-sm font-medium text-foreground">
      <span>{label}</span>
      <FdeSelect ariaLabel={label} value={value} onValueChange={onChange} disabled={disabled} options={placeholder === undefined ? options : [{ value: '', label: placeholder }, ...options]} className="mt-1.5" />
    </div>
  )
}

export function MultiChoice({ label, values, options, onChange }: {
  label: string
  values: string[]
  options: Array<{ id: string; name: string }>
  onChange: (values: string[]) => void
}) {
  return (
    <fieldset>
      <Label className="text-sm">{label}</Label>
      <div className="mt-2 grid max-h-36 gap-2 overflow-y-auto rounded-lg border border-border bg-muted/20 p-3 sm:grid-cols-2">
        {options.map((option) => (
          <label key={option.id} className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={values.includes(option.id)}
              onChange={(event) => onChange(event.target.checked ? [...values, option.id] : values.filter((id) => id !== option.id))}
              className="size-4 rounded border-input accent-primary"
            />
            <span className="truncate">{option.name}</span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}
